import { Component, Element, h, Host, Listen, Prop, State } from "@stencil/core";

import { debounce } from "../../utils/debounce";

@Component({
    tag: "ux-text-lines-limit",
    styleUrl: "text-lines-limit.scss",
    shadow: false
})
export class TextLinesLimit {
    private baseClass = "ux-text-lines-limit";

    private textElement: HTMLElement;
    private textElementText: string;
    private textElementContainer: HTMLElement;

    @Element() element: HTMLUxTextLinesLimitElement;

    private parentTab: HTMLUxTabElement;
    private parentTabId: string | null;

    /** sets the lines of text to be displayed on small viewports */
    @Prop() readonly textLinesS: number = 0;
    /** sets the lines of text to be displayed on medium viewports */
    @Prop() readonly textLinesM: number = 0;
    /** sets the lines of text to be displayed on large viewports */
    @Prop() readonly textLinesL: number = 0;
    /** sets the lines of text to be displayed on xl viewports */
    @Prop() readonly textLinesXl: number = 0;
    /**  sets the lines of text to be displayed on all viewports */
    @Prop() readonly textLines: number | null = null;
    /** css selector of nested element to be cropped */
    @Prop() readonly nestedElementSelector: false | string = false;
    /** if set, the compoent will display a button with this text,
     * upon clicking on the button, the whole text will be displayed to the user
     */
    @Prop() readonly readMoreText: string = undefined;
    /** if set, the comonent will try to expand to the bottom and add more lines
     * than the ones indicated via props, if there is space at the bottom
     */
    @Prop() readonly expand: boolean = false;
    /** if set, if the text lines are less then the desired crop, the component Will
     * not set a defined height to the text
     */
    @Prop() readonly collapse: boolean = false;

    @State() croppedDescriptionText: string;
    @State() maxHeight: number;
    @State() effectiveHeight: string;
    @State() isCropDisabled = false;

    private cropText = () => {
        if (this.textElement === undefined || this.textElement === null || this.isCropDisabled) {
            return;
        }
        if (this.maxHeight === null) {
            this.textElement.innerHTML = this.textElementText;
            return;
        }
        let isCropped = false;
        const ellipses = `...`;
        const croppedSuffix = this.readMoreText ? `${ellipses} ${this.readMoreText}` : ellipses;
        this.croppedDescriptionText = this.textElementText.split(" ").reduce((output: string, word: string): string => {
            if (typeof output !== "string" || output.length === 0) {
                return word;
            }
            if (isCropped) {
                return output;
            }
            let text = output + " " + word;
            this.textElement.innerHTML = text + croppedSuffix;
            if (this.textElementContainer.scrollHeight > this.maxHeight + 5) {
                text = output + ellipses;
                isCropped = true;
            }
            this.textElement.innerHTML = text;
            if (this.readMoreText && this.readMoreText.length > 0 && isCropped) {
                this.textElement.insertAdjacentHTML(
                    "beforeend",
                    // eslint-disable-next-line max-len
                    `&nbsp;<button type="button" data-ux-text-lines-limit="read-more" style="padding:0;" class="ux-text-link ux-text-link--green">${this.readMoreText}</button>`
                );
            }
            return text;
        });
    };

    /** debounced version of crop text used for components in the slider where there could be
     * reinitialisations of the component
     */
    private debouncedCropText = debounce(() => this.cropText(), 100);

    private getCurrentBreakpointHeight = (): number => {
        const screenWidth = window.innerWidth;
        if (this.textLines !== null) {
            return this.textLines;
        }
        if (screenWidth < 768) {
            return this.textLinesS;
        } else if (screenWidth < 1024) {
            return this.textLinesM;
        } else if (screenWidth < 1440) {
            return this.textLinesL;
        } else {
            return this.textLinesXl;
        }
    };

    private getElementsHeights = (fullHeight = false) => {
        if (this.isCropDisabled) {
            return;
        }
        const elementStyles = window.getComputedStyle(this.textElement);
        const fontSize = elementStyles.getPropertyValue("font-size");
        const lineHeight = elementStyles.getPropertyValue("line-height");
        let singleLineHeight = parseFloat(lineHeight);
        if (singleLineHeight < 3) {
            singleLineHeight = parseFloat(fontSize) * singleLineHeight;
        }
        const marginTop = parseFloat(elementStyles.getPropertyValue("margin-top"));
        const marginBottom = parseFloat(elementStyles.getPropertyValue("margin-bottom"));
        const paddingTop = parseFloat(elementStyles.getPropertyValue("padding-top"));
        const paddingBottom = parseFloat(elementStyles.getPropertyValue("padding-bottom"));
        const maxHeight = !fullHeight
            ? this.getCurrentBreakpointHeight() !== 0
                ? singleLineHeight * this.getCurrentBreakpointHeight()
                : null
            : this.element.getBoundingClientRect().height;
        this.maxHeight = maxHeight !== null ? maxHeight + paddingTop + paddingBottom : null;
        // detracting margins in full height mode to prevent content overlfow
        this.maxHeight = fullHeight ? this.maxHeight - marginTop - marginBottom : this.maxHeight;
        if (this.collapse) {
            this.maxHeight =
                this.textElement.scrollHeight + 10 > singleLineHeight * this.getCurrentBreakpointHeight() ? this.maxHeight : null;
        }
        this.effectiveHeight =
            this.maxHeight !== null && !fullHeight
                ? `${paddingTop + paddingBottom + maxHeight + marginTop + marginBottom}px`
                : "auto";
        this.textElementContainer.style.maxHeight = this.maxHeight !== null ? `${this.maxHeight}px` : "100%";
    };

    @State() expandElement = false;

    private triggerExpansion = () => {
        this.effectiveHeight = "100%";
        this.expandElement = true;
    };

    private performCrop = debounce(() => {
        this.getElementsHeights();
        this.cropText();
        if (this.expand) {
            this.triggerExpansion();
        }
    }, 200);

    componentDidRender() {
        if (this.expandElement) {
            this.getElementsHeights(true);
            this.debouncedCropText();
            this.expandElement = false;
        }
    }

    @Listen("uxActiveTabSet", { target: "window" })
    cropOnTabChange(e: CustomEvent) {
        if (this.parentTab === undefined || this.parentTab === null) {
            return;
        }
        if (this.parentTabId === e.detail) {
            this.performCrop();
        }
    }

    @State() initialTextSet = false;

    private setElements = () => {
        this.textElementContainer = this.element.querySelector("*:first-child");
        this.textElement = this.element.querySelector(this.nestedElementSelector || "*:first-child");
        if (!this.initialTextSet) {
            this.textElementText = this.textElement.innerHTML;
            this.initialTextSet = true;
        }
    };

    @Listen("uxActiveTabSet", { target: "window" })
    componentWillLoad() {
        this.parentTab = this.element.closest("ux-tab");
        this.parentTabId = this.parentTab !== null ? this.parentTab.tabId : null;
        if (this.element.closest("ux-slider")) {
            return;
        }
        this.getElementsHeights();
        if (this.parentTab !== null && !this.parentTab?.classList.contains("is-active")) {
            return;
        }
        this.cropText();
        if (this.expand) {
            this.triggerExpansion();
        }
    }

    @Listen("uxSliderInitialised", { target: "document" })
    cropOnSliderIntialised(customEvent: CustomEvent) {
        const parentSlider = this.element.closest("ux-slider");
        if (!parentSlider) {
            return;
        }
        const parentSliderId = parentSlider.getAttribute("slider-id");
        if (customEvent.detail.id === parentSliderId) {
            this.getElementsHeights();
            this.debouncedCropText();
            if (this.expand) {
                this.triggerExpansion();
            }
        }
    }

    private handleClick = (e: Event) => {
        const eTarget = e.target as HTMLElement;
        if (eTarget.getAttribute("data-ux-text-lines-limit") === "read-more") {
            this.effectiveHeight = "auto";
            this.textElementContainer.style.maxHeight = "100%";
            this.textElement.innerHTML = this.textElementText;
            this.isCropDisabled = true;
            e.preventDefault();
        }
    };

    connectedCallback() {
        this.setElements();
        window.addEventListener("resize", this.performCrop);
    }

    disconnectedCallback() {
        window.removeEventListener("resize", this.performCrop);
    }

    render() {
        return (
            <Host
                style={{ height: this.effectiveHeight }}
                class={this.baseClass}
                onClick={this.readMoreText ? this.handleClick : null}
            >
                <slot />
            </Host>
        );
    }
}
